import { Component, Injectable, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, UrlSegment } from '@angular/router';
import { Subscription } from 'rxjs';
import { DataView } from 'primeng/dataview';
import { DialogService, UtilService, MomentService, PagedResult } from 'src/app/shared';
import { NavigationService } from 'src/app/shared/navigation/navigation.service';
import { EventSearchParams } from '../shared/event-search-params.model';
import { EventsV4Service } from '../shared/events-v4.service';
import { EventV4TypeService } from '../shared/event-type/event-v4-type.service';
import { EventsV4NewComponent } from '../new/events-v4-new.component';
import { WlEventsV4ToolbarBroadcastService, WlEventsV4ToolbarEvent, WlEventsV4ToolbarEventTypeEnum } from 'src/app/shared/container/toolbars/module/events-v4/broadcast';

@Component({
  selector: 'wl-events-v4-dashboard',
  templateUrl: './events-v4-dashboard.component.html',
  styleUrls: ['./events-v4-dashboard.component.less'],
  standalone: false,
})
@Injectable()
export class EventsV4DashboardComponent implements OnInit, OnDestroy {
  @ViewChild('upcomingEventsDataGrid', { static: false }) public upcomingEventsDataGrid: DataView;
  @ViewChild('newEventDialog', { static: false }) newEventDialog: EventsV4NewComponent;

  public static _lastSearch: string = '';

  public _lastSearchParams: any;
  public _dataInitiallyLoaded: boolean;
  public _eventTypes: Array<string> = [];
  public _subscriptions: Array<Subscription> = [];
  public _exporting: boolean = false;
  public _exportEmpty: boolean = false;
  public _error: string = '';
  public selectedEventType: string;

  public errorMessage: string;
  public upcomingEvents: PagedResult;
  public searchParams: EventSearchParams;
  public isSearching: boolean;
  public _resumeSearchInitially: boolean = false;

  public constructor(
    private _route: ActivatedRoute,
    private _dialogService: DialogService,
    private _utilService: UtilService,
    private _momentService: MomentService,
    private _navigationService: NavigationService,
    private _eventToolbarBroadcastService: WlEventsV4ToolbarBroadcastService,
    private _eventsService: EventsV4Service,
    private _eventTypeService: EventV4TypeService
  ) {
    this._dataInitiallyLoaded = false;

    this.upcomingEvents = new PagedResult();
    this.errorMessage = '';

    let storedSearchParams = this.getStoredSearchParameters();

    if (storedSearchParams) {
      this.searchParams = storedSearchParams;
      // simple JSON object with the date coming back as a string. Create a new date here.
      this.searchParams.SearchDateBegin = new Date(storedSearchParams.SearchDateBegin);
      this.searchParams.SearchDateEnd = new Date(storedSearchParams.SearchDateEnd);
      this._resumeSearchInitially = true;
    } else {
      this.searchParams = new EventSearchParams();
      this.searchParams.SearchDateBegin = this._momentService.getMoment().startOf('day').toDate();
      this.searchParams.SearchDateEnd = this._momentService.getMoment().add(1, 'years').startOf('day').toDate();
      this.searchParams.PageNumber = 1;
      this.searchParams.PageSize = 10;
    }

    this.isSearching = false;

    this._lastSearchParams = null;
  }

  ngOnInit() {
    this._eventTypeService.getEventTypes().then((data: Array<string>) => {
      this._eventTypes = data;
    });

    this._route.url.subscribe((data: Array<UrlSegment>) => {
      for (let seg of data) {
        if (this._utilService.toLowerTrim(seg.path) == 'new') {
          //directly open the new dialog
          setTimeout(() => this.newEvent(), 500);
        }
      }
    });

    // watch for passed in query params
    this._subscriptions.push(
      this._route.queryParams.subscribe((params: any) => {
        if (params) {
          if (!this._resumeSearchInitially) this.searchParams.Keyword = params.q;
          this.search();
        }
      })
    );

    // watch for if the tool bar reset button was pressed
    this._subscriptions.push(
      this._eventToolbarBroadcastService.subscribe((toolbarEvent) => {
        if (toolbarEvent.type == WlEventsV4ToolbarEventTypeEnum.clearKeyword) {
          this.resetForm();
          this.search();
        } else if (toolbarEvent.type == WlEventsV4ToolbarEventTypeEnum.requestKeywordAnswer) {
          this.searchParams.Keyword = toolbarEvent.data;
          this._search();
        }
      })
    );

    // if coming back from somewhere else, resume the search where they left off
    if (this._resumeSearchInitially) setTimeout(() => this.resumeSearch());
  }

  /**
   * clean up
   */
  ngOnDestroy(): void {
    if (this._subscriptions) this._subscriptions.forEach((s) => s.unsubscribe());

    // ensure everyone watching that we're no longer searching
    this._eventToolbarBroadcastService.broadcast(new WlEventsV4ToolbarEvent(WlEventsV4ToolbarEventTypeEnum.searching, false));
  }

  public loadEvents = (paging?: any): Promise<any> => {
    this.isSearching = true;
    this.searchParams.OrderBy = 'StartDateTimeUTC:asc';
    this._eventToolbarBroadcastService.broadcast(new WlEventsV4ToolbarEvent(WlEventsV4ToolbarEventTypeEnum.searching, true));

    if (paging) {
      this.searchParams.PageNumber = paging.PageNumber;
      this.searchParams.PageSize = paging.PageSize;
    }
    this.searchParams.PageNumber = Math.max(1, this.searchParams.PageNumber);

    this._resumeSearchInitially = false;

    // save search parameters in case they leave and come back
    this.storeSearchParameters(this.searchParams);

    return this._eventsService.searchEvents(this.searchParams).then(
      (data: any) => {
        this.isSearching = false;
        this.upcomingEvents = data;
        this._eventToolbarBroadcastService.broadcast(new WlEventsV4ToolbarEvent(WlEventsV4ToolbarEventTypeEnum.searching, false));
      },
      (error: any) => {
        this.isSearching = false;
        this._dialogService.error(error);
        this._eventToolbarBroadcastService.broadcast(new WlEventsV4ToolbarEvent(WlEventsV4ToolbarEventTypeEnum.searching, false));
      }
    );
  };

  public lazyLoad($event: any): void {
    // don't let the grid load first if user is coming back to the page
    if (!this._resumeSearchInitially) this.loadEvents({ PageNumber: $event.first / Math.max(1, $event.rows) + 1, PageSize: $event.rows });
  }

  public searchChanged = (newSearchParams: any): void => {
    this.searchParams = newSearchParams;
    this.loadEvents();
  };

  public _search(): void {
    (<any>this.upcomingEventsDataGrid).first = 0;
    this.searchParams.PageNumber = 1;
    this.searchParams.PageSize = 10;
    this.loadEvents();
  }

  /**
   * initialize a new search
   */
  public search(): void {
    // need keyword
    this._eventToolbarBroadcastService.broadcast(new WlEventsV4ToolbarEvent(WlEventsV4ToolbarEventTypeEnum.requestKeyword));
  }

  /**
   * initialize a search
   */
  public resumeSearch(): void {
    // set the grid's paginator to the save page number
    (<any>this.upcomingEventsDataGrid).first = (this.searchParams.PageNumber - 1) * this.upcomingEventsDataGrid.rows;
    //this.upcomingEventsDataGrid.updatePaginator();
    this.loadEvents();
  }

  /**
   * start an export of the current search, unpaged
   */
  public export(): void {
    if (this._exporting) return;

    this._exporting = true;
    this._exportEmpty = false;
    this._error = '';
    this._eventsService.getEventsExport(this.searchParams).then(
      (url) => {
        this._exporting = false;
        if (url) this._navigationService.popupSafeNavigate(url);
        else this._exportEmpty = true;
      },
      (error) => {
        this._exporting = false;
        this._error = this._utilService.extractErrorMessage(error);
      }
    );
  }

  /**
   * rest form to default
   */
  public resetForm(): void {
    this.searchParams.Keyword = '';
    this.searchParams.EventType = [];
    this.searchParams.OrganizationEvent = this.searchParams.InternalEvent = this.searchParams.MembersOnlyEvent = true;
    this.searchParams.CommunityEvent = false;
    this.searchParams.EventsWithPendingRegistrationsOnly = false;
    this.searchParams.SearchDateBegin = this._momentService.getMoment().startOf('day').toDate();
    this.searchParams.SearchDateEnd = this._momentService.getMoment().add(1, 'years').startOf('day').toDate();
  }

  public handleSearchSubmit(event?: any): void {
    this.searchParams.EventType = this.selectedEventType ? [this.selectedEventType] : [];
    this.search();
  }

  public gotoEvent(eventId: number): void {
    this._navigationService.navigateToEventV4(eventId);
  }

  public gotoEventSettings(eventId: number): void {
    this._navigationService.navigateToEventV4Settings(eventId);
  }

  public newEvent = (): void => {
    this.newEventDialog.reset();
    this.newEventDialog.open();
  };

  /**
   * store given search parameters
   */
  public storeSearchParameters(searchParamsToSave: EventSearchParams): void {
    EventsV4DashboardComponent._lastSearch = JSON.stringify(searchParamsToSave);
  }

  /**
   * get stored search parameters
   */
  public getStoredSearchParameters(): EventSearchParams {
    if (!EventsV4DashboardComponent._lastSearch) return null;

    let reconstitutedSearchParams: EventSearchParams = null;

    try {
      reconstitutedSearchParams = JSON.parse(EventsV4DashboardComponent._lastSearch);

      if (reconstitutedSearchParams.SearchDateBegin && typeof reconstitutedSearchParams.SearchDateBegin == 'string')
        reconstitutedSearchParams.SearchDateBegin = this._momentService.getMomentByDate(reconstitutedSearchParams.SearchDateBegin).toDate();
      if (reconstitutedSearchParams.SearchDateEnd && typeof reconstitutedSearchParams.SearchDateEnd == 'string')
        reconstitutedSearchParams.SearchDateEnd = this._momentService.getMomentByDate(reconstitutedSearchParams.SearchDateEnd).toDate();

      return reconstitutedSearchParams;
    } catch (e) {
      // shut upuh you face
    }

    return reconstitutedSearchParams;
  }
}
